import React, { useState } from "react";
import { Alert, Autocomplete, Box, Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatarMoeda, formatarNumero, formatarPercentual, lerNumero, montarTabelaPosicao } from "../utils/societarioCalculos";
import { estiloBotaoAcao, estiloBotaoAdicionar, estiloCabecalhoInterno, estiloCampoInline, estiloCelulaInterna } from "../utils/societarioEstilos";

// ==========================================================
// ABA POSIÇÃO SOCIETÁRIA
// "Antes" vem da alteração anterior (regra 1) e "depois" é
// antes + movimentos. Só a carga inicial tem posição digitada.
// ==========================================================

const linhaVazia = { socioId: null, quotas: "", valor: "" };

export default function SocietarioPosicao({ alteracao, alteracoes, socios, somenteLeitura, onChangePosicaoInicial }) {
  const [novaLinha, setNovaLinha] = useState({ ...linhaVazia });
  const [erroLinha, setErroLinha] = useState("");

  const nomeSocio = (id) => socios.find((s) => String(s.id) === String(id))?.nomeRazao || `Sócio #${id}`;
  const posicaoInicial = alteracao.posicaoInicial || [];
  const tabela = montarTabelaPosicao(alteracao, alteracoes);

  const adicionarLinha = () => {
    const quotas = lerNumero(novaLinha.quotas);
    const valor = lerNumero(novaLinha.valor);
    if (!novaLinha.socioId) return setErroLinha("Selecione o sócio.");
    if (posicaoInicial.some((p) => String(p.socioId) === String(novaLinha.socioId))) return setErroLinha("Sócio já está na posição inicial.");
    if (quotas === null || quotas < 0) return setErroLinha("Informe as quotas.");
    if (valor === null || valor < 0) return setErroLinha("Informe o valor em R$.");
    setErroLinha("");
    onChangePosicaoInicial([...posicaoInicial, { socioId: novaLinha.socioId, quotas: String(novaLinha.quotas), valor: String(novaLinha.valor) }]);
    setNovaLinha({ ...linhaVazia });
  };

  const removerLinha = (socioId) => onChangePosicaoInicial(posicaoInicial.filter((p) => String(p.socioId) !== String(socioId)));

  const celulaNumero = { ...estiloCelulaInterna, textAlign: "right", whiteSpace: "nowrap" };
  const cabecalhoNumero = { ...estiloCabecalhoInterno, textAlign: "right" };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {alteracao.cargaInicial ? (
        <Box>
          <Typography variant="subtitle2" sx={{ color: "var(--ig-t-cccccc)", fontWeight: 600, mb: 1 }}>
            Carga inicial — posição de partida (antes desta alteração)
          </Typography>
          <Alert severity="info" sx={{ mb: 1 }}>
            Primeira alteração lançada para esta empresa: informe a posição societária de partida conforme o contrato anterior. Para constituição, deixe sem
            sócios e lance os aumentos de capital na aba Movimentos.
          </Alert>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ width: "100%", minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...estiloCabecalhoInterno, width: "55%" }}>Sócio</TableCell>
                  <TableCell sx={cabecalhoNumero}>Quotas</TableCell>
                  <TableCell sx={cabecalhoNumero}>Valor (R$)</TableCell>
                  {!somenteLeitura && <TableCell sx={{ ...estiloCabecalhoInterno, textAlign: "right" }}>Ações</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {posicaoInicial.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-666666)", fontStyle: "italic", py: 1.5, textAlign: "center" }}
                    >
                      Nenhum sócio na posição de partida.
                    </TableCell>
                  </TableRow>
                )}
                {posicaoInicial.map((p) => (
                  <TableRow key={p.socioId} sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                    <TableCell sx={estiloCelulaInterna}>{nomeSocio(p.socioId)}</TableCell>
                    <TableCell sx={celulaNumero}>{formatarNumero(lerNumero(p.quotas), 6)}</TableCell>
                    <TableCell sx={celulaNumero}>{formatarMoeda(lerNumero(p.valor))}</TableCell>
                    {!somenteLeitura && (
                      <TableCell align="right" sx={estiloCelulaInterna}>
                        <Tooltip title="Remover sócio da posição inicial">
                          <IconButton size="small" onClick={() => removerLinha(p.socioId)} sx={estiloBotaoAcao("#d32f2f", "#c62828")}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!somenteLeitura && (
                  <TableRow sx={{ backgroundColor: "var(--ig-f-1a1a1a)" }}>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                      <Autocomplete
                        size="small"
                        sx={{ minWidth: 240 }}
                        options={socios.filter((s) => s.ativo && !posicaoInicial.some((p) => String(p.socioId) === String(s.id)))}
                        getOptionLabel={(s) => `${s.nomeRazao} (${s.documentoExibicao || s.documento})`}
                        value={socios.find((s) => String(s.id) === String(novaLinha.socioId)) || null}
                        onChange={(_, val) => setNovaLinha((p) => ({ ...p, socioId: val?.id ?? null }))}
                        renderInput={(params) => <TextField {...params} placeholder="Selecionar sócio" size="small" sx={estiloCampoInline} />}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                      <TextField
                        size="small"
                        placeholder="Quotas"
                        value={novaLinha.quotas}
                        onChange={(e) => setNovaLinha((p) => ({ ...p, quotas: e.target.value }))}
                        slotProps={{ htmlInput: { inputMode: "decimal", style: { textAlign: "right" } } }}
                        sx={{ minWidth: 120, ...estiloCampoInline }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                      <TextField
                        size="small"
                        placeholder="R$"
                        value={novaLinha.valor}
                        onChange={(e) => setNovaLinha((p) => ({ ...p, valor: e.target.value }))}
                        slotProps={{ htmlInput: { inputMode: "decimal", style: { textAlign: "right" } } }}
                        sx={{ minWidth: 120, ...estiloCampoInline }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                      <Button size="small" startIcon={<AddIcon />} onClick={adicionarLinha} sx={estiloBotaoAdicionar}>
                        Adicionar
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          {erroLinha && <Typography sx={{ color: "#f44336", fontSize: "0.8rem", mt: 0.5 }}>{erroLinha}</Typography>}
        </Box>
      ) : (
        <Alert severity="info">
          A posição <b>antes</b> é calculada automaticamente a partir do saldo final da alteração anterior desta empresa e não pode ser editada.
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ color: "var(--ig-t-cccccc)", fontWeight: 600, mb: 1 }}>
          Posição societária — antes × depois (depois = antes + movimentos)
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ width: "100%", minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={estiloCabecalhoInterno} rowSpan={2}>
                  Sócio
                </TableCell>
                <TableCell sx={{ ...estiloCabecalhoInterno, textAlign: "center", borderLeft: "1px solid var(--ig-b-444444)" }} colSpan={4}>
                  Antes
                </TableCell>
                <TableCell sx={{ ...estiloCabecalhoInterno, textAlign: "center", borderLeft: "1px solid var(--ig-b-444444)", color: "#1998a8" }} colSpan={4}>
                  Depois
                </TableCell>
              </TableRow>
              <TableRow>
                {["Quotas", "Valor", "R$/Quota", "%", "Quotas", "Valor", "R$/Quota", "%"].map((t, i) => (
                  <TableCell key={i} sx={{ ...cabecalhoNumero, borderLeft: i === 0 || i === 4 ? "1px solid var(--ig-b-444444)" : undefined }}>
                    {t}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tabela.linhas.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-666666)", fontStyle: "italic", py: 1.5, textAlign: "center" }}
                  >
                    Sem posição societária. Selecione a empresa e lance a posição inicial ou os movimentos.
                  </TableCell>
                </TableRow>
              )}
              {tabela.linhas.map((l) => {
                const mudou = Math.abs(l.depois.quotas - l.antes.quotas) > 0.000001 || Math.abs(l.depois.valor - l.antes.valor) > 0.000001;
                return (
                  <TableRow key={l.socioId} sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                    <TableCell sx={estiloCelulaInterna}>{nomeSocio(l.socioId)}</TableCell>
                    <TableCell sx={{ ...celulaNumero, borderLeft: "1px solid var(--ig-b-333333)", color: "var(--ig-t-cccccc)" }}>
                      {formatarNumero(l.antes.quotas, 6)}
                    </TableCell>
                    <TableCell sx={{ ...celulaNumero, color: "var(--ig-t-cccccc)" }}>{formatarMoeda(l.antes.valor)}</TableCell>
                    <TableCell sx={{ ...celulaNumero, color: "var(--ig-t-cccccc)" }}>{formatarNumero(l.antes.valorQuota, 4)}</TableCell>
                    <TableCell sx={{ ...celulaNumero, color: "var(--ig-t-cccccc)" }}>{formatarPercentual(l.antes.percentual)}</TableCell>
                    <TableCell sx={{ ...celulaNumero, borderLeft: "1px solid var(--ig-b-333333)", color: mudou ? "var(--ig-t-64b5f6)" : "var(--ig-t-ffffff)" }}>
                      {formatarNumero(l.depois.quotas, 6)}
                    </TableCell>
                    <TableCell sx={{ ...celulaNumero, color: mudou ? "var(--ig-t-64b5f6)" : "var(--ig-t-ffffff)" }}>{formatarMoeda(l.depois.valor)}</TableCell>
                    <TableCell sx={celulaNumero}>{formatarNumero(l.depois.valorQuota, 4)}</TableCell>
                    <TableCell sx={celulaNumero}>{formatarPercentual(l.depois.percentual)}</TableCell>
                  </TableRow>
                );
              })}
              {tabela.linhas.length > 0 && (
                <TableRow sx={{ backgroundColor: "var(--ig-f-1a1a1a)" }}>
                  <TableCell sx={{ ...estiloCelulaInterna, fontWeight: "bold" }}>Total</TableCell>
                  <TableCell sx={{ ...celulaNumero, borderLeft: "1px solid var(--ig-b-333333)", fontWeight: "bold" }}>
                    {formatarNumero(tabela.totalAntes.quotas, 6)}
                  </TableCell>
                  <TableCell sx={{ ...celulaNumero, fontWeight: "bold" }}>{formatarMoeda(tabela.totalAntes.valor)}</TableCell>
                  <TableCell sx={{ ...celulaNumero, fontWeight: "bold" }}>{formatarNumero(tabela.totalAntes.valorQuota, 4)}</TableCell>
                  <TableCell sx={{ ...celulaNumero, fontWeight: "bold" }}>{tabela.totalAntes.quotas > 0 ? "100%" : "—"}</TableCell>
                  <TableCell sx={{ ...celulaNumero, borderLeft: "1px solid var(--ig-b-333333)", fontWeight: "bold" }}>
                    {formatarNumero(tabela.totalDepois.quotas, 6)}
                  </TableCell>
                  <TableCell sx={{ ...celulaNumero, fontWeight: "bold" }}>{formatarMoeda(tabela.totalDepois.valor)}</TableCell>
                  <TableCell sx={{ ...celulaNumero, fontWeight: "bold" }}>{formatarNumero(tabela.totalDepois.valorQuota, 4)}</TableCell>
                  <TableCell sx={{ ...celulaNumero, fontWeight: "bold" }}>{tabela.totalDepois.quotas > 0 ? "100%" : "—"}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}
